import { FC, useEffect, useState } from 'react';
import { Card, Divider, RadixTable } from '@features/ui';
import { periodOptions } from '@features/admin/reports/reportData';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportFilters } from '@hooks/useReportFilters';
import { ReportPeriod } from '@src/types/reports';
import { toast } from 'react-toastify';
import axios from 'axios';
import { formatCurrency } from '@src/lib/utils';
import dayjs from 'dayjs';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

type TOldAffiliateByUsersData = {
  username: string;
  total_commissions: number;
};

type TOldAffiliateByUsersResponse = {
  username?: string;
  total_commissions?: number | string | null;
};

export const OldAffiliateByUsers: FC = () => {
  const [data, setData] = useState<TOldAffiliateByUsersData[]>([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.TODAY,
    enableComparisons: false,
  });

  const getReportData = async () => {
    filters.setIsLoading(true);
    try {
      const isTodayPeriod = filters.selectedPeriod === ReportPeriod.TODAY;
      const todayDate = filters.endDate ? dayjs(filters.endDate) : dayjs();
      const payload = isTodayPeriod
        ? {
            start_date: todayDate.format('YYYY-MM-DD'),
            end_date: todayDate.format('YYYY-MM-DD'),
          }
        : {
            start_date: filters.startDate
              ? dayjs(filters.startDate).format('YYYY-MM-DD')
              : null,
            end_date: filters.endDate
              ? dayjs(filters.endDate).format('YYYY-MM-DD')
              : null,
          };

      const response = await axios.post(
        '/reports/amember/affiliate-commissions/',
        payload
      );

      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data;

      const normalizedData = Array.isArray(responseData)
        ? responseData.map((item: TOldAffiliateByUsersResponse) => ({
            username: item.username ?? '-',
            total_commissions: Number.isFinite(Number(item.total_commissions))
              ? Number(item.total_commissions)
              : 0,
          }))
        : [];

      setData(normalizedData);
    } catch (error) {
      toast.error('Failed to fetch report data. Please try again later.');
      console.error('Error fetching report data:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const { columns, tableData } =
    useReportTableColumns<TOldAffiliateByUsersData>({
      data,
      getAccessField: (item) => item.username,
      getMainDataField: (item) => item.total_commissions,
      customColumns: [
        {
          title: 'Total Commissions',
          className: 'text-right',
          render: (item) => {
            if (!item) return null;

            return (
              <p className="text-white">
                {formatCurrency(item.total_commissions, 2)}
              </p>
            );
          },
        },
      ],
      includeTotalsRow: true,
      totalsRowFormatter: (totals) => ({
        ...totals,
        total_commissions: formatCurrency(totals.total_commissions, 2),
      }),
    });

  useEffect(() => {
    getReportData();
  }, []);

  return (
    <div className="flex flex-col gap-5">
      <h1 className="text-2xl font-semibold text-white">
        Old Affiliate Commission Amount by User
      </h1>
      <Card className="p-6">
        <ReportFilteringSettings
          showPeriodFilter
          dateRangeValue={filters.valueStart}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedPeriod={filters.selectedPeriod}
          showGroupByFilter={false}
          onDateRangeChange={filters.setValueStart}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={getReportData}
        />
        <Divider className="my-4" />
        <RadixTable columns={columns} data={tableData} />
      </Card>
    </div>
  );
};
